'use client';

import { useState } from 'react';
import { Play } from 'lucide-react';

import type { LangueVitrine } from '@/features/marketing/i18n';

import { DEMO_VIDEO_ID, DEMO_VIDEO_TITLE } from '../landingContent';
import { Container, SectionHeading } from './ui';

/**
 * Vidéo de démonstration (#808).
 *
 * **Façade, pas iframe.** Un embed YouTube posé directement charge son lecteur
 * et ses cookies de mesure sur chaque visite, y compris pour les visiteurs qui
 * ne lanceront jamais la vidéo — sur une page qui affirme deux sections plus bas
 * héberger les données en France et instruire les demandes RGPD, c'est
 * exactement le genre de contradiction qu'un DPO relève. Rien de tiers n'est
 * appelé avant le clic, et le domaine utilisé ensuite est `youtube-nocookie`.
 *
 * La section ne se rend pas tant qu'aucun identifiant n'est renseigné.
 */
/*
  ⚠️ La langue est **acceptée sans être employée** : la section ne se rend pas tant qu'aucune vidéo
  n'est renseignée (`DEMO_VIDEO_ID`), et son titre viendra de la vidéo elle-même. La prendre
  aujourd'hui évite d'avoir à toucher l'accueil le jour où elle en aura besoin.
*/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function VideoSection({ langue: _langue = 'fr' }: { langue?: LangueVitrine }) {
  const [playing, setPlaying] = useState(false);

  if (!DEMO_VIDEO_ID) return null;

  return (
    <section id="video" className="bg-white pt-4 pb-20 lg:pb-24">
      <Container>
        <SectionHeading
          eyebrow="Démonstration"
          title="Voir le produit avant d’en parler"
          lead="Le parcours complet d’un mois de portage, du compte-rendu d’activité du consultant à la facture client."
        />

        <div className="mt-12 overflow-hidden rounded-3xl border border-slate-200/70 bg-slate-950 shadow-2xl shadow-slate-300/50">
          <div className="relative aspect-video w-full">
            {playing ? (
              <iframe
                src={`https://www.youtube-nocookie.com/embed/${DEMO_VIDEO_ID}?autoplay=1&rel=0`}
                title={DEMO_VIDEO_TITLE}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                className="absolute inset-0 size-full"
              />
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label={`Lire la vidéo : ${DEMO_VIDEO_TITLE}`}
                className="from-primary-800 via-primary-900 group absolute inset-0 flex size-full flex-col items-center justify-center gap-5 bg-gradient-to-br to-slate-950"
              >
                <span className="from-primary-400 to-primary-600 shadow-primary-900/40 flex size-16 items-center justify-center rounded-full bg-gradient-to-br text-white shadow-xl transition-transform group-hover:scale-110">
                  <Play className="size-6 translate-x-0.5 fill-current" />
                </span>
                <span className="text-[15px] font-semibold text-white">{DEMO_VIDEO_TITLE}</span>
                <span className="text-[12px] text-white/45">
                  La vidéo se charge depuis YouTube au clic, pas avant.
                </span>
              </button>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
